import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useDataService } from '@/lib/service-context';
import type { NavKind } from './nav-prefs-catalog';

export const NAV_PREFS_KEY = 'erp_nav_open_in_new_tab';

type EntryPrefs = Partial<Record<NavKind, boolean>>;

export interface NavPrefsValue {
  v: 1;
  entries: Record<string, EntryPrefs>;
}

const DEFAULT_PREFS: NavPrefsValue = { v: 1, entries: {} };

function loadFromLocal(): NavPrefsValue {
  try {
    const raw = localStorage.getItem(NAV_PREFS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && parsed.v === 1 && typeof parsed.entries === 'object') {
        return parsed as NavPrefsValue;
      }
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_PREFS;
}

export interface BulkUpdate {
  id: string;
  kind: NavKind;
  open: boolean;
}

interface Ctx {
  prefs: NavPrefsValue;
  shouldOpenInNewTab(id: string, kind: NavKind): boolean;
  setEntry(id: string, kind: NavKind, open: boolean): void;
  setBulk(updates: BulkUpdate[]): void;
  resetAll(): void;
}

const NavPrefsContext = createContext<Ctx | null>(null);

export function NavPrefsProvider({ children }: { children: ReactNode }) {
  const ds = useDataService();
  const [prefs, setPrefs] = useState<NavPrefsValue>(loadFromLocal);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    ds.userPreferences.get(NAV_PREFS_KEY).then((val) => {
      if (!val || typeof val !== 'object') return;
      const v = val as Partial<NavPrefsValue>;
      if (v.v === 1 && v.entries && typeof v.entries === 'object') {
        const next = v as NavPrefsValue;
        setPrefs(next);
        try { localStorage.setItem(NAV_PREFS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      }
    }).catch(() => { /* ignore — keep localStorage value */ });
  }, [ds]);

  const persist = useCallback((next: NavPrefsValue) => {
    try { localStorage.setItem(NAV_PREFS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    ds.userPreferences.set(NAV_PREFS_KEY, next).catch(() => { /* ignore — localStorage already holds it */ });
  }, [ds]);

  const setEntry = useCallback((id: string, kind: NavKind, open: boolean) => {
    setPrefs((p) => {
      const nextEntry: EntryPrefs = { ...(p.entries[id] ?? {}), [kind]: open };
      // Trim falsy values to keep the JSONB compact.
      if (!open) delete nextEntry[kind];
      const nextEntries = { ...p.entries };
      if (Object.keys(nextEntry).length === 0) delete nextEntries[id];
      else nextEntries[id] = nextEntry;
      const next: NavPrefsValue = { v: 1, entries: nextEntries };
      persist(next);
      return next;
    });
  }, [persist]);

  const setBulk = useCallback((updates: BulkUpdate[]) => {
    setPrefs((p) => {
      const nextEntries = { ...p.entries };
      for (const u of updates) {
        const cur = nextEntries[u.id] ?? {};
        const merged: EntryPrefs = { ...cur };
        if (u.open) merged[u.kind] = true;
        else delete merged[u.kind];
        if (Object.keys(merged).length === 0) delete nextEntries[u.id];
        else nextEntries[u.id] = merged;
      }
      const next: NavPrefsValue = { v: 1, entries: nextEntries };
      persist(next);
      return next;
    });
  }, [persist]);

  const resetAll = useCallback(() => {
    const next: NavPrefsValue = { v: 1, entries: {} };
    persist(next);
    setPrefs(next);
  }, [persist]);

  const value = useMemo<Ctx>(() => ({
    prefs,
    shouldOpenInNewTab: (id, kind) => Boolean(prefs.entries[id]?.[kind]),
    setEntry,
    setBulk,
    resetAll,
  }), [prefs, setEntry, setBulk, resetAll]);

  return <NavPrefsContext.Provider value={value}>{children}</NavPrefsContext.Provider>;
}

export function useNavPrefs(): Ctx {
  const ctx = useContext(NavPrefsContext);
  if (!ctx) throw new Error('useNavPrefs must be used within <NavPrefsProvider>');
  return ctx;
}
